<template>
  <!-- prettier-ignore -->
  <form
    v-form-tracker="validator"
    novalidate
    v-bind="$attrs"
    :name="name"
    @submit.prevent="debouncedSubmit">
    <slot></slot>
  </form>
</template>

<script>
import { cloneDeep, debounce } from 'lodash'

import { ValidatorsHelper } from '@/helpers/Validators'
import FormTracker from '@/directives/FormTracker'

import {
  EVENT_FORM_INIT,
  EVENT_FORM_INTERACTED,
  EVENT_FORM_CANCELED,
  EVENT_FORM_ERROR,
  EVENT_FORM_SUBMITTED,
} from '@/config/events'
import { DEFAULT_FORM_DEBOUNCE_TIME } from '@/config/constants'

export default {
  name: 'OvpForm',

  directives: { FormTracker },

  props: {
    name: {
      type: String,
      required: true,
    },

    // trigger submit even if form is not dirty
    noChangeDetection: {
      type: Boolean,
      default: false,
    },

    trackingDisabled: {
      type: Boolean,
      default: false,
    },

    validator: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      debouncedSubmit: () => {},
    }
  },

  created() {
    this.debouncedSubmit = debounce(() => this.submit(), DEFAULT_FORM_DEBOUNCE_TIME)
  },

  mounted() {
    if (!this.trackingDisabled) {
      this.$EventBus.$emit(EVENT_FORM_INIT, this.payload)
    }
  },

  computed: {
    payload() {
      return {
        form: this.name,
        errors: this.yieldErrors(),
      }
    },
  },

  methods: {
    handleAction() {
      const isDirty = cloneDeep(this.validator.$anyDirty)

      this.validator.$touch()

      if (!this.validator.$invalid) {
        // if dirty (or change detection has been disabled). trigger submit
        if (this.noChangeDetection || isDirty) {
          this.triggerSubmit(isDirty)
        } else {
          this.triggerCancel(isDirty)
        }
      } else {
        this.scrollToError()
        this.triggerError(false)
      }
    },

    submit() {
      ValidatorsHelper.pendingValidate(this.validator)
        .then(() => this.handleAction())
        .catch(() => this.handleAction())
    },

    triggerCancel(dirty) {
      if (!this.trackingDisabled) {
        this.$EventBus.$emit(EVENT_FORM_CANCELED, this.payload)
      }

      this.validator.$reset()
      this.$emit('cancel', { dirty })
    },

    triggerError(touch = true) {
      if (touch) {
        this.validator.$touch()
      }
      if (!this.trackingDisabled) {
        this.$EventBus.$emit(EVENT_FORM_ERROR, this.payload)
      }
    },

    triggerInteraction(fieldName) {
      const payload = {
        ...this.payload,
        fieldName,
      }

      if (!this.trackingDisabled) {
        this.$EventBus.$emit(EVENT_FORM_INTERACTED, payload)
      }
    },

    scrollToError() {
      this.$nextTick(() => {
        const element = this.$el.querySelector('.input--invalid')
        const offset = element?.offsetTop
        if (element && offset > 0) {
          window.scrollTo({
            top: element.offsetTop - 50,
            behavior: 'smooth',
          })
        }
      })
    },

    triggerSubmit(dirty) {
      if (!this.trackingDisabled) {
        this.$EventBus.$emit(EVENT_FORM_SUBMITTED, this.payload)
      }

      this.$emit('submit', { dirty })
    },

    yieldErrors() {
      const fields = Object.keys(this.validator.$params)
      const data = {}

      fields.forEach(field => {
        const params = Object.keys(this.validator[field].$params)

        params.forEach(param => {
          const valid = this.validator[field][param]

          if (!valid) {
            if (!data[field]) data[field] = []
            data[field].push(param)
          }
        })
      })

      let str = ''
      Object.keys(data).forEach(key => {
        str += `${key}:${data[key].join(',')};`
      })

      return str
    },
  },
}
</script>
